"use client";

import React, { useEffect, useState } from "react";
import { UserEvents, trackEvent } from "@/analytics";

import { Special } from "@/data/specials";
import StarsIcon from "./icons/StarsIcon";
import { createClientComponentClient } from "@/utils/supabase/client";
import { formatPrice } from "@/components/common/utils";
import { getUserCanAccessContent } from "@/data/specials-utils";
import { userIsSubscribedToComedian } from "@/data/subscriptions";

export default function UnlockPill({
  price,
  className,
  special,
  userFollowsComedian,
  userPurchasedSpecial,
  comedianId,
  isLiveVideo,
}: {
  price: number;
  className: string;
  special: Special;
  userFollowsComedian: boolean;
  userPurchasedSpecial: boolean;
  comedianId: string;
  isLiveVideo: boolean;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [userCanAccessSpecial, setUserCanAccessSpecial] = useState(false);
  let content = "";

  switch (true) {
    case special.requiresSubscription && !userCanAccessSpecial:
      content = `Subscribe to Watch`;
      break;
    case price > 0 && !userCanAccessSpecial:
      content = `Watch${isLiveVideo ? " Live" : ""} for $${formatPrice(price)}`;
      break;
    case userCanAccessSpecial && isLiveVideo:
      content = "Watch Live!";
      break;
    case userCanAccessSpecial:
      content = "Continue Watching...";
      break;
    default:
      content = "Watch for Free!";
  }

  useEffect(() => {
    const supabase = createClientComponentClient();
    const fetchSubscriptionStatus = async () => {
      setIsLoading(true);
      const isUserSubscribed = await userIsSubscribedToComedian(
        supabase,
        comedianId
      );
      const userCanAccessSpecial = getUserCanAccessContent({
        special,
        userFollowsComedian,
        userPurchasedSpecial,
        isUserSubscribed,
      });
      setUserCanAccessSpecial(userCanAccessSpecial);
      setIsLoading(false);
    };
    fetchSubscriptionStatus();
  }, [comedianId, special, userFollowsComedian, userPurchasedSpecial]);

  if (isLoading) {
    return (
      <div
        className={`${className} animate-pulse flex items-center justify-center bg-gradient-to-r from-neutral-700 to-neutral-800 pointer-events-none`}
      >
        <div className="w-5 h-5 rounded-full bg-neutral-600 mr-4"></div>
        <div className="h-5 bg-neutral-600 rounded-full w-36"></div>
      </div>
    );
  }

  return (
    <button
      className={className}
      onClick={() =>
        trackEvent(UserEvents.CLICKED_WATCH_FEATURED_SPECIAL, {
          CTAButtonText: content,
          ComedianId: comedianId,
          SpecialId: special.id,
          SpecialPrice: price,
        })
      }
    >
      <StarsIcon />
      <span>{content}</span>
    </button>
  );
}
